import { useCallback } from 'react';
import { isTabEnabledIn, useNavConfig } from '../api/Navigation/moduleConfig';

/**
 * Tab-level visibility for a page whose tabs an Operator can configure
 * (see `pageTabGroups` in navigationModules.ts).
 *
 * Tab config rides on the SAME per-company page-config storage as pages:
 * rows carry `moduleId = groupId` and `pagePath = tabId`.
 *
 * Behaviour, for a tab WITHOUT the registry's demo marker (deliberately fail-open, matching the
 * page/module filters):
 * - config still loading, the read failed, or no rows for this group  => the tab is shown
 * - a tab with no row                                                  => shown
 * - a tab marked `alwaysEnabled` in the registry                       => shown, always
 * Failing open means a config-fetch hiccup degrades to "too much UI", never to an empty page.
 *
 * A tab WITH the demo marker is the opposite (CO-851): hidden while the config is loading, hidden
 * when the read failed or came back empty, and shown only when a successful read holds no OFF row
 * for it. A demo tab is a finished-looking screen with nothing behind it, so "too much UI" is the
 * failure there. The rule itself lives in the store (`isTabEnabledIn`), not here.
 *
 * 🔴 FAILING OPEN WHILE LOADING IS VISIBLE, and callers must handle it. `isTabEnabled` answers
 * `true` for every real tab until `loaded` flips, so a caller that renders its strip regardless
 * paints the FULL list of real tabs and then drops the disabled ones a tick later. Gate the strip
 * on `loaded`. (`UserProfile` did not, which is exactly what the flash on that page was.)
 *
 * 🔴 READS THE SHARED STORE (`useNavConfig`), synchronously. A remount after the config has landed
 * (each `/profile/:id/<tab>` is its own route, so the profile shell remounts on every tab switch)
 * costs no request and has no fail-open frame to correct: the first paint is already the filtered
 * one. Reading the store also means an Operator's change reaches an open tab strip through the
 * `ModuleConfigUpdated` broadcast, which the hook this replaced never listened to.
 */
export function usePageTabConfig(groupId: string) {
  const navConfig = useNavConfig();
  // True once a read has come back for this user, good or failed, or nobody is signed in.
  const loaded = navConfig.settled;

  const { pages, pagesOk } = navConfig.state;

  // Keyed on the page rows alone, so a module-only change does not hand callers a new function.
  const isTabEnabled = useCallback(
    (tabId: string): boolean => isTabEnabledIn({ pages, pagesOk }, groupId, tabId),
    [pages, pagesOk, groupId]
  );

  return { isTabEnabled, loaded };
}
